import { CanvasSource, Texture } from "pixi.js";
import type { JellyAsset } from "../content/types";

export function jellyMaterialKey(asset: JellyAsset): string {
  const p = asset.parameters;
  return JSON.stringify([p.color, p.highlight, p.opacity, asset.source]);
}

/** One texture per material per owner. Callers dispose the bank after destroying views. */
export class JellyTextures {
  private cache = new Map<string, Promise<Texture>>();
  private disposed = false;
  async prepare(asset: JellyAsset): Promise<Texture> {
    if (this.disposed) throw new Error("纹理库已销毁");
    const key = jellyMaterialKey(asset);
    let result = this.cache.get(key);
    if (!result) {
      result = this.rasterize(asset);
      this.cache.set(key, result);
      result.catch(() => this.cache.delete(key));
    }
    return result;
  }
  private async rasterize(asset: JellyAsset): Promise<Texture> {
    const p = asset.parameters;
    const rim = [16, 8, 0].reduce(
      (color, shift) =>
        color | (Math.round(((p.color >> shift) & 255) * 0.55) << shift),
      0,
    );
    const svgDocument = new DOMParser().parseFromString(
      asset.source,
      "image/svg+xml",
    );
    const root = svgDocument.documentElement;
    if (
      svgDocument.querySelector("parsererror") ||
      root.localName !== "svg" ||
      root.namespaceURI !== "http://www.w3.org/2000/svg"
    ) {
      throw new Error("身体资源必须是合法 SVG");
    }
    const highlights = svgDocument.querySelectorAll('[id="highlight-primary"]');
    if (highlights.length !== 1)
      throw new Error("身体资源必须包含唯一的 highlight-primary");
    // Set CSS variables on the parsed SVG: whitespace, quote style and attribute order
    // in the source are irrelevant. Existing opacity is replaced, never duplicated.
    const style = (root as unknown as SVGSVGElement).style;
    style.setProperty(
      "--body-color",
      `#${p.color.toString(16).padStart(6, "0")}`,
    );
    style.setProperty("--rim-color", `#${rim.toString(16).padStart(6, "0")}`);
    style.setProperty("--body-opacity", String(p.opacity));
    (highlights[0] as SVGElement).style.setProperty(
      "opacity",
      String(p.highlight),
    );
    const source = new XMLSerializer().serializeToString(svgDocument);
    const url = URL.createObjectURL(
      new Blob([source], { type: "image/svg+xml" }),
    );
    try {
      const image = new Image();
      image.src = url;
      await image.decode();
      if (this.disposed) throw new Error("纹理库已销毁");
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = 448;
      canvas.getContext("2d")!.drawImage(image, 0, 0, 448, 448);
      // 身体在游戏里远小于源图；仅双线性采样会漏采轮廓与高光边缘。
      // 静态材质上传时生成 mipmaps，所有同材质实例共享，不逐帧重建。
      return new Texture({
        source: new CanvasSource({
          resource: canvas,
          autoGenerateMipmaps: true,
          scaleMode: "linear",
        }),
      });
    } finally {
      URL.revokeObjectURL(url);
    }
  }
  destroy() {
    this.disposed = true;
    for (const value of this.cache.values())
      void value.then(
        (t) => t.destroy(true),
        () => {},
      );
    this.cache.clear();
  }
}
